import { IShopNavigationMenu, NAVIGATION_SLUGS } from '@components/shared/shop/ShopNavigationMenu';
import { useTheme } from '@components/theme';
import classNames from 'classnames';
import CategoryMenuDropList from '@components/shared/shop/ShopNavigationMenu/CategoryMenuDropdownList/CategoryMenuDropdownList';
import MenuDropDownList from '@components/shared/shop/ShopNavigationMenu/MenuDropDownList/MenuDropDownList';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import PrismicImage from '@components/shared/images/atoms/Image/PrismicImage';
import breakpoints from '@constants/breakpoints';
import React, { MutableRefObject, useContext } from 'react';
import { SHOP_MENU_ANIMATION_TIMEOUT } from '@components/shared/shop/ShopNavigationMenu/ShopNavigationMenu';
import { userIsDesignerOrAdmin } from '@lib/auth/auth';
import UserContext from '@context/User/user-context';
import { navigationHeights } from '../../../../../styles/variables';
import styles from './DropDownMenu.module.scss';

interface DropDownMenuProps {
  animateDropdownContent: boolean;
  shopNavigationMenu: IShopNavigationMenu | null;
  isNavOpen: boolean;
  setAnimateDropdownContent: (v: boolean) => void;
  timeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  setActiveDropdownMenu: (v: null) => void;
  unbounceBannerHeight: number;
}

export default function DropDownMenu({
  shopNavigationMenu,
  animateDropdownContent,
  isNavOpen,
  setAnimateDropdownContent,
  timeoutRef,
  setActiveDropdownMenu,
  unbounceBannerHeight
}: DropDownMenuProps) {
  if (!shopNavigationMenu || !shopNavigationMenu.option?.is_dropdown_enabled) return null;

  const { user } = useContext(UserContext);
  const isAdminOrDesigner = userIsDesignerOrAdmin(user);

  const theme = useTheme();
  const { item, option } = shopNavigationMenu;
  const hasCategoryChildren = item.categoryChildren && item?.categoryChildren?.length > 0;

  return (
    <div
      className={classNames(theme.styles.Body, styles.DropdownWrapper, {
        [styles.CategoryMenu]: hasCategoryChildren,
        [styles.Leaving]: !isNavOpen
      })}
      id="shop-dropdown"
      onMouseLeave={() => {
        setAnimateDropdownContent(false);
        // eslint-disable-next-line no-param-reassign
        timeoutRef.current = setTimeout(() => {
          setAnimateDropdownContent(true);
          setActiveDropdownMenu(null);
        }, SHOP_MENU_ANIMATION_TIMEOUT);
      }}
      style={{
        marginTop: unbounceBannerHeight || 'initial',
        top: `calc(${isAdminOrDesigner ? Number(navigationHeights.navHeight) - 3 : navigationHeights.mainNavHeight}px + ${navigationHeights.shopNavigationHeight}px)`
      }}
    >
      <div className={`${styles.Content} ${animateDropdownContent ? '' : styles.Leaving}`}>
        {hasCategoryChildren ? (
          <CategoryMenuDropList navItem={item} />
        ) : (
          <MenuDropDownList
            className={styles.DropDownListWrapper}
            navItem={item}
            navOption={option}
          />
        )}
        {option?.is_dropdown_enabled && (
          <div className={styles.ImagesWrapper}>
            <a
              role="button"
              href={option?.image1_cta_link}
              className={styles.ImageButton}
              onClick={() => {
                analyticsTrack('shop_nav_image_clicked', {
                  title: `Shop Navigation Menu | Image | ${option.image1_cta_label}`,
                  ctaUrl: option.image1_cta_link,
                  image: option.image1
                });
              }}
            >
              <PrismicImage
                className={styles.Image}
                image={option.image1}
                breakpoints={{
                  desktop: breakpoints.desktop
                }}
                lazy={false}
              />
              <p>{option?.image1_cta_label}</p>
            </a>
            {option.image2 && item.slug !== NAVIGATION_SLUGS.FURNITURE && (
              <a
                role="button"
                href={option?.image2_cta_link}
                className={styles.ImageButton}
                onClick={() => {
                  analyticsTrack('shop_nav_image_clicked', {
                    title: `Shop Navigation Menu | Image | ${option.image2_cta_label}`,
                    ctaUrl: option.image2_cta_link,
                    image: option.image2
                  });
                }}
              >
                <PrismicImage
                  className={styles.Image}
                  image={option.image2}
                  breakpoints={{ desktop: breakpoints.desktop }}
                  lazy={false}
                />
                <p>{option?.image2_cta_label}</p>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
